import { useNavigate } from '@tanstack/react-router';
import {
  ArrowRightLeft,
  Check,
  Cog,
  EllipsisVertical,
  LogOut,
  Plus,
  Settings,
  Sparkles,
} from 'lucide-react';
import { toast } from 'sonner';

import { setLastWorkspaceId } from '../utils/lastWorkspace';
import { CharacterAvatar } from './CharacterAvatar';

import { AuthService } from '@/entities/auth/auth-service';
import { useUser } from '@/entities/auth/hooks/use-user';
import { USER_ROLE } from '@/entities/user/constants';
import type { User } from '@/entities/user/types';
import { WORKSPACE_PLAN } from '@/entities/workspace/constants';
import { Button } from '@/shared/components/shadcn/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/shared/components/shadcn/dropdown-menu';

export function NavigationUserInfo({
  isCollapsed,
  plan,
  workspaceId,
}: {
  isCollapsed: boolean;
  plan: WORKSPACE_PLAN;
  workspaceId: string;
}) {
  const { user } = useUser();
  if (!user) return null;

  if (isCollapsed) {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <CharacterAvatar name={user.first_name} />
        </DropdownMenuTrigger>
        <DropdownMenus user={user} plan={plan} workspaceId={workspaceId} />
      </DropdownMenu>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <div className="flex w-full cursor-pointer items-center gap-2 rounded-xl p-2 transition-all hover:bg-zinc-100">
          <UserInfo user={user} />
          <Button variant="ghost" size="icon" className="size-4">
            <EllipsisVertical />
          </Button>
        </div>
      </DropdownMenuTrigger>
      <DropdownMenus user={user} plan={plan} workspaceId={workspaceId} />
    </DropdownMenu>
  );
}

const UserInfo = ({ user }: { user: User }) => {
  return (
    <div className="flex w-full items-center gap-2">
      <CharacterAvatar name={user.first_name} />
      <div className="flex flex-1 flex-col overflow-hidden">
        <span className="truncate text-sm font-medium">
          {user.first_name} {user.last_name}
        </span>
        <span className="text-muted-foreground truncate text-xs">{user.email}</span>
      </div>
    </div>
  );
};

const DropdownMenus = ({
  user,
  plan,
  workspaceId,
}: {
  user: User;
  plan: WORKSPACE_PLAN;
  workspaceId: string;
}) => {
  const navigate = useNavigate();
  const hasMyWorkspace = user.workspaces.some(
    (workspace) => workspace.privilege === USER_ROLE.OWNER,
  );

  const isAdmin = user.email.includes('@deepbluedot.io');

  const changeWorkspace = (workspaceId: string) => {
    setLastWorkspaceId(user.id, workspaceId);
    navigate({ to: `/workspace/${workspaceId}` });
  };

  return (
    <DropdownMenuContent align="end" className="w-56" side="right" sideOffset={16}>
      <DropdownMenuLabel>
        <UserInfo user={user} />
      </DropdownMenuLabel>
      <DropdownMenuSeparator />
      {plan === WORKSPACE_PLAN.TRIAL && (
        <>
          <DropdownMenuItem
            className="bg-primary text-primary-foreground"
            onClick={() =>
              navigate({
                to: '/workspace/$workspaceId/settings/plan',
                params: {
                  workspaceId,
                },
              })
            }
          >
            <Sparkles className="text-primary-foreground" />
            Upgrade
          </DropdownMenuItem>
          <DropdownMenuSeparator />
        </>
      )}
      {isAdmin && (
        <DropdownMenuItem onClick={() => navigate({ to: '/workspace/internal' })}>
          {workspaceId === 'internal' ? <Check className="size-4" /> : <Cog className="size-4" />}
          Internal Workspace
        </DropdownMenuItem>
      )}
      {[...user.workspaces]
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((workspace) => (
          <DropdownMenuItem key={workspace.id} onClick={() => changeWorkspace(workspace.id)}>
            {workspaceId === workspace.id ? (
              <Check className="size-4" />
            ) : (
              <ArrowRightLeft className="size-4" />
            )}
            {workspace.name}
          </DropdownMenuItem>
        ))}
      <DropdownMenuSeparator />
      {(!hasMyWorkspace || isAdmin) && (
        <DropdownMenuItem onClick={() => navigate({ to: '/workspace/create' })}>
          <Plus />
          New workspace
        </DropdownMenuItem>
      )}

      <DropdownMenuItem
        onClick={() =>
          navigate({
            to: '/workspace/$workspaceId/settings/general',
            params: {
              workspaceId,
            },
          })
        }
      >
        <Settings />
        Settings
      </DropdownMenuItem>
      <DropdownMenuItem
        onClick={async () => {
          await AuthService.logout();
          toast.success('Logged out');
          navigate({ to: '/login' });
        }}
      >
        <LogOut />
        Logout
      </DropdownMenuItem>
    </DropdownMenuContent>
  );
};
